import { useParams } from 'react-router-dom';
import useBillingService from '../billingService';
import { BillingScheduleItemResponse } from '../billingTypes';
import { formatChangeRequestAlias, formatPeriod, formatCustomPeriod } from '../billingUtils';
import { CreateForm, FormFieldsData } from 'framework/form';
import { useBillingRouteContext } from '../BillingRouteContext';
import { useNewProjectUrl } from 'domain/shared/newProject';
import UpdateInvoiceDatesContent from './UpdateInvoiceDatesContent';
import type { InvoiceDateFormItem } from './types';

const sortByPosition = (a: BillingScheduleItemResponse, b: BillingScheduleItemResponse): number =>
  Number(a.position) - Number(b.position);

const getItemDisplayName = (item: BillingScheduleItemResponse): string => {
  if ('changeRequestAlias' in item && item.changeRequestAlias) {
    return formatChangeRequestAlias(item.changeRequestAlias);
  }
  if ('milestoneName' in item && item.milestoneName) {
    const alias = 'alias' in item ? item.alias : '';
    return `${alias} ${item.milestoneName}`.trim() || item.milestoneName;
  }
  if ('periodMonth' in item && item.periodMonth) {
    return formatPeriod(item.position, item.periodMonth);
  }
  if ('startDate' in item && 'endDate' in item) {
    return formatCustomPeriod(item.startDate, item.endDate);
  }
  return ('alias' in item && item.alias) || `Item ${item.position}`;
};

const mapScheduleItemToFormItem = (item: BillingScheduleItemResponse): InvoiceDateFormItem => ({
  id: item.id,
  invoiceDate: item.invoiceDate,
  position: item.position,
  name: getItemDisplayName(item),
  amount: 'amount' in item ? item.amount : null,
  currency: 'currency' in item ? item.currency : null,
  type: item.type,
});

const UpdateInvoiceDatesLayout = () => {
  const { billingScheduleId } = useParams<{ billingScheduleId: string }>();
  const { projectId } = useBillingRouteContext();
  const { getFinanceUrl } = useNewProjectUrl();
  const { billingScheduleSearch, updateInvoiceDates } = useBillingService();

  const getDataFunc = async () => {
    const response = await billingScheduleSearch({
      pageSize: 1,
      filter: { id: { eq: billingScheduleId! } },
    });
    const schedule = response?.result?.[0];
    if (!schedule?.scheduleItems?.length) {
      return { items: [] };
    }
    const sortedItems = [...schedule.scheduleItems].sort(sortByPosition);
    return { items: sortedItems.map(mapScheduleItemToFormItem) };
  };

  const submitFormFunc = async (data: FormFieldsData) => {
    const items = data.items as InvoiceDateFormItem[];
    const payload = {
      items: items.map(({ id, invoiceDate }) => ({
        id,
        invoiceDate: invoiceDate || null,
      })),
    };

    await updateInvoiceDates(billingScheduleId!, payload);
  };

  return (
    <CreateForm
      getDataFunc={getDataFunc}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        redirect: () => getFinanceUrl(projectId),
        message: 'You are awesome! The invoice dates have been successfully updated.',
      }}
    >
      <UpdateInvoiceDatesContent />
    </CreateForm>
  );
};

export default UpdateInvoiceDatesLayout;
